import React from 'react';
import { ThemeProvider, Box } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

import CalendarMain from './CalendarMain';
import EventDialog from './EventDialog';
import MobileNavigation from './MobileNavigation';
import FilterSidebar from './FilterSidebar';
import LoadingSpinner from '../Common/LoadingSpinner';
import ErrorMessage from '../Common/ErrorMessage';

import { useAuth } from '../../hooks/useAuth';
import { useCalendarState } from '../../hooks/useCalendarState';
import { useCalendarEvents } from '../../hooks/useCalendarEvents';

import { theme } from '../../utils/colors';

const FullCalendarComponent = (props) => {
  const { user: parentUser = null, isDesk: parentIsDesk = null, onSignOut: parentSignOut = () => {} } = props;
  // レスポンシブ判定: スマホ(～767px) / タブレット(768～1023px) / PC(1024px～)
  const isMobile = useMediaQuery('(max-width:767px)');
  const isTablet = useMediaQuery('(min-width:768px) and (max-width:1023px)');

  // 認証情報は parentUser があれば優先利用し、無い場合のみ useAuth で取得する。
  // useAuth の読み込みでカレンダー描画をブロックしないことで初期表示の体感速度を改善する。
  const { currentUser, isLoading: isAuthLoading, displayName } = useAuth();
  const calendarState = useCalendarState(currentUser);
  const calendarEvents = useCalendarEvents(currentUser, calendarState.autoUpdate);

  // Authenticator 配下でレンダリングされるため、parentUser が存在すれば認証済みとみなす。
  // 内部の認証取得失敗時のみエラー表示にフォールバックする。
  const isAuthenticatedFromParent = Boolean(parentUser);
  if (!isAuthenticatedFromParent) {
    if (isAuthLoading) return <LoadingSpinner />;
    if (!currentUser || !currentUser.isAuthenticated) return <ErrorMessage message="ログインしてください" />;
  }

  // 親/内部どちらかで Desks 判定が取れればデスク扱いにする
  const effectiveIsDesk = Boolean(parentIsDesk || currentUser?.isDesk || (currentUser?.groups || []).includes('Desks'));
  const effectiveUser = parentUser ?? currentUser;
  const effectiveSignOut = parentSignOut;

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: (isMobile || isTablet) ? 'column' : 'row',
          maxWidth: 1920,
          mx: 'auto',
          bgcolor: 'background.paper',
          boxShadow: 3,
        }}
      >
        <CalendarMain
          isMobile={isMobile}
          isTablet={isTablet}
          calendarState={calendarState}
          calendarEvents={calendarEvents}
          currentUser={effectiveUser}
        />

        <EventDialog
          calendarState={calendarState}
          calendarEvents={calendarEvents}
          isDesk={effectiveIsDesk}
          // 親からの user（parentUser）があれば優先して渡す
          currentUser={parentUser ?? currentUser}
          displayName={displayName}
        />

        {(isMobile || isTablet) && <MobileNavigation calendarState={calendarState} />}

        <FilterSidebar
          isMobile={isMobile}
          isTablet={isTablet}
          calendarState={calendarState}
          user={effectiveUser}
          isDesk={effectiveIsDesk}
          onSignOut={effectiveSignOut}
        />
      </Box>
    </ThemeProvider>
  );
};

export default FullCalendarComponent;
